import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import dayjs from "dayjs";
import { useState } from "react";
import { useValidationDetailQuery } from "../api/selectValidationDetail";
import type { ValidationStatusT } from "../api/selectValidationStatus";

interface Props {
    status: ValidationStatusT | undefined;
}

function badgeClasses(kind: "none" | "stale" | "approved" | "rejected") {
    switch (kind) {
        case "stale":
            return "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200";
        case "approved":
            return "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200";
        case "rejected":
            return "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200";
        default:
            return "bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-100";
    }
}

function DetailPopoverContent({ validationId, open }: { validationId: string; open: boolean }) {
    const { data: detail, isLoading } = useValidationDetailQuery(validationId, open);

    if (isLoading || !detail) {
        return <p className="text-muted-foreground text-sm">Loading...</p>;
    }

    const signedOffByExternal = Boolean(detail.on_behalf_of_name);

    return (
        <div className="space-y-2 text-sm">
            <div>
                <div className="text-muted-foreground text-xs">
                    {detail.is_valid ? "Approved" : "Rejected"}
                    {detail.validated_at ? ` · ${dayjs(detail.validated_at).format("D MMM YYYY, HH:mm")}` : ""}
                </div>
            </div>
            {signedOffByExternal ? (
                <div>
                    <div className="font-medium">{detail.on_behalf_of_name}</div>
                    {detail.on_behalf_of_organisation ? (
                        <div className="text-muted-foreground text-xs">{detail.on_behalf_of_organisation}</div>
                    ) : null}
                    <div className="mt-1 text-muted-foreground text-xs">
                        Recorded by {detail.validated_by_name || detail.validated_by_email || "an admin"}
                    </div>
                </div>
            ) : (
                <div>
                    <div className="font-medium">{detail.validated_by_name || "Unnamed validator"}</div>
                    {detail.validated_by_email ? (
                        <div className="text-muted-foreground text-xs">{detail.validated_by_email}</div>
                    ) : null}
                </div>
            )}
            {!detail.is_valid && detail.reason ? (
                <div className="pt-1 border-t">
                    <div className="text-muted-foreground text-xs">Reason</div>
                    <div>{detail.reason}</div>
                </div>
            ) : null}
        </div>
    );
}

/** Admin/builder-facing only - not part of the validator's own to-do list. */
const ValidationStatusBadge = ({ status }: Props) => {
    const [open, setOpen] = useState(false);

    if (!status || status.validated_at === null || !status.validation_id) {
        return (
            <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs", badgeClasses("none"))}>
                Not validated
            </span>
        );
    }

    const kind = status.is_stale ? "stale" : status.is_valid ? "approved" : "rejected";
    const label = status.is_stale ? "Stale - edited since validation" : status.is_valid ? "Validated" : "Rejected";

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs cursor-pointer hover:opacity-80",
                        badgeClasses(kind)
                    )}
                >
                    {label}
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72">
                <DetailPopoverContent validationId={status.validation_id} open={open} />
            </PopoverContent>
        </Popover>
    );
};

export default ValidationStatusBadge;
